import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Gamepad2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import CartDrawer from "@/components/CartDrawer";
import Footer from "@/sections/Footer";
import { Eyebrow, MaskedLine, Reveal } from "@/components/Reveal";

const GAMES = [
  {
    to: "/affirmation-game",
    testid: "game-card-affirmations",
    img: "/images/affirm-brave.png",
    title: "I AM… Affirmation Game",
    text: "Match each affirmation to its friend and earn your Affirmation Star badge.",
    tag: "4 rounds · ages 3+",
    bg: "linear-gradient(160deg, #FFF4CC 0%, #FFFDF2 100%)",
  },
  {
    to: "/dentist-game",
    testid: "game-card-dentist",
    img: "/images/dentist-alldone.png",
    title: "Bailey Visits the Dentist",
    text: "Help Bailey through his check-up, pick tooth-kind foods and win your sticker.",
    tag: "3 rounds · ages 2–7",
    bg: "linear-gradient(160deg, #DFF5EE 0%, #FFFDF2 100%)",
  },
  {
    to: "/starts-school-game",
    testid: "game-card-school",
    img: "/images/school-bailey.png",
    title: "Bailey Starts School",
    text: "Join Bailey's first day — coat pegs, story time, music and new friends.",
    tag: "4 rounds · ages 3–6",
    bg: "linear-gradient(160deg, #DDEAFB 0%, #FFFDF2 100%)",
  },
  {
    to: "/what-can-you-do-game",
    testid: "game-card-wcyd",
    img: "/images/wcyd-football.png",
    title: "Bailey, What Can You Do?",
    text: "Football, getting dressed, tidying up, painting — find everything Bailey can do!",
    tag: "4 rounds · ages 2–6",
    bg: "linear-gradient(160deg, #FDEBE0 0%, #FFFDF2 100%)",
  },
];

export default function GamesPage() {
  return (
    <div className="grain">
      <Navbar />
      <main className="pt-28 sm:pt-32 pb-24 overflow-x-clip relative">
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none z-0">
          {[
            { top: "7%", left: "72%", delay: "0.4s", duration: "5s" },
            { top: "3%", left: "42%", delay: "2.4s", duration: "6s" },
            { top: "14%", left: "88%", delay: "4s", duration: "5.4s" },
            { top: "24%", left: "58%", delay: "5.6s", duration: "7s" },
            { top: "9%", left: "24%", delay: "7.2s", duration: "6.4s" },
          ].map((s, i) => (
            <span
              key={i}
              className="shooting-star"
              style={{ top: s.top, left: s.left, "--shoot-delay": s.delay, "--shoot-duration": s.duration }}
            />
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Eyebrow className="mb-5">Play with Bailey</Eyebrow>
            <h1 className="font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-walnut">
              <MaskedLine i={0}>Bailey's</MaskedLine>
              <MaskedLine i={1} className="text-terracotta">Games</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 text-base sm:text-lg text-walnutsoft"
            >
              Little games from Bailey's books — pick one and play!
            </motion.p>
          </div>

          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {GAMES.map((g, i) => (
              <Reveal key={g.to} delay={i * 0.1}>
                <Link to={g.to} data-testid={g.testid} className="group block h-full">
                  <div
                    className="h-full rounded-[2rem] border-2 border-sand p-7 sm:p-8 shadow-[0_20px_40px_-20px_rgba(49,67,92,0.25)] group-hover:-translate-y-2 group-hover:shadow-[0_30px_50px_-20px_rgba(49,67,92,0.35)] transition-all duration-300"
                    style={{ background: g.bg }}
                  >
                    <img
                      src={g.img}
                      alt={g.title}
                      className="h-40 sm:h-48 mx-auto object-contain drop-shadow-[0_14px_18px_rgba(49,67,92,0.25)] group-hover:scale-105 group-hover:-rotate-2 transition-transform duration-300"
                    />
                    <h2 className="mt-5 font-display font-extrabold tracking-tight text-xl sm:text-2xl text-walnut text-center">{g.title}</h2>
                    <p className="mt-2 text-sm sm:text-base text-walnutsoft text-center leading-relaxed">{g.text}</p>
                    <p className="mt-4 flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-amberbrand">
                      <Gamepad2 size={14} /> {g.tag}
                    </p>
                    <p className="mt-4 text-center">
                      <span className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream font-display font-bold text-sm px-7 py-3 group-hover:bg-terracottadark transition-colors">
                        Play now
                      </span>
                    </p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </main>
      <Footer />
      <CartDrawer />
    </div>
  );
}
